/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import * as color from '@constant/color';
import { useTypedDispatch } from '@hook/common/useStore';
import { useInhouseSigninMutation } from '@hook/auth/useApi';
import { actions as userActions } from '@store/slice/user-slice';
import { ADMIN_PATH } from '@constant/admin/route-path';
import { EService, EUserRole } from '@datatype/type';
import { EAdminApiEnv, ADMIN_API_ENV_STORAGE_KEY } from '@config/environment';
import TextInput from '@component/common/input/text-input';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${color.GRAY_5};
  padding: 24px;
  box-sizing: border-box;
`;

const Card = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 360px;
  padding: 32px 24px;
  border-radius: 12px;
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const InputBox = styled.div`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  box-sizing: border-box;
  border: 1px solid ${color.GRAY_2};
  border-radius: 8px;
  display: flex;
  align-items: center;
`;

const ErrorText = styled.div`
  min-height: 18px;
  color: ${color.RED_2};
  font-size: 13px;
`;

const EnvRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 8px;
  width: 100%;
`;

const EnvButton = styled.button<{ $selected: boolean }>`
  all: unset;
  flex: 1;
  height: 36px;
  border: 1px solid ${(props) => (props.$selected ? color.BROWN_1 : color.GRAY_2)};
  border-radius: 8px;
  background-color: ${(props) => (props.$selected ? color.BROWN_1 : color.WHITE_1)};
  color: ${(props) => (props.$selected ? color.WHITE_1 : color.BLACK_3)};
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
`;

const INPUT_DESIGN = {
  text: { size: 'medium' as const, color: color.BLACK_2 },
  box: { width: '100%', height: '100%' },
};

const Signin: React.FC = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const inhouseSigninMutation = useInhouseSigninMutation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [apiEnv, setApiEnv] = useState<EAdminApiEnv>(
    localStorage.getItem(ADMIN_API_ENV_STORAGE_KEY) === EAdminApiEnv.PROD ? EAdminApiEnv.PROD : EAdminApiEnv.LOCAL,
  );

  const handleEnvChange = (env: EAdminApiEnv) => {
    if (env === apiEnv) return;
    localStorage.setItem(ADMIN_API_ENV_STORAGE_KEY, env);
    setApiEnv(env);
    window.location.reload();
  };

  const handleSignin = async () => {
    if (!username.trim() || !password.trim()) {
      setErrorMessage('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    setErrorMessage(null);
    try {
      const response = await inhouseSigninMutation.mutateAsync({
        service: EService.OTTERBRICK,
        username,
        password,
      });
      const data = response?.data;
      if (!data) {
        setErrorMessage('로그인에 실패했습니다.');
        return;
      }

      const role = data.role as EUserRole | undefined | null;
      if (role !== EUserRole.ADMIN) {
        setErrorMessage('관리자 권한이 없습니다.');
        return;
      }

      dispatch(
        userActions.authenticate({
          userKey: data.key,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          role: EUserRole.ADMIN,
        }),
      );
      navigate(ADMIN_PATH.HUB_PATH, { replace: true });
    } catch (err) {
      setErrorMessage('로그인에 실패했습니다.');
    }
  };

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') handleSignin();
  };

  return (
    <Wrapper>
      <Card>
        <TextBlock
          text='Otterbrick Admin'
          design={{
            text: { size: 'large', color: color.BLACK_2, bold: true },
            box: { alignX: 'center', padding: '0 0 8px 0' },
          }}
        />
        <EnvRow>
          <EnvButton $selected={apiEnv === EAdminApiEnv.LOCAL} onClick={() => handleEnvChange(EAdminApiEnv.LOCAL)}>
            Local
          </EnvButton>
          <EnvButton $selected={apiEnv === EAdminApiEnv.PROD} onClick={() => handleEnvChange(EAdminApiEnv.PROD)}>
            Prod
          </EnvButton>
        </EnvRow>
        <InputBox onKeyDown={handleKeyDown}>
          <TextInput
            name='username'
            placeholder='아이디'
            value={username}
            onChange={(ev) => setUsername(ev.target.value)}
            design={INPUT_DESIGN}
          />
        </InputBox>
        <InputBox onKeyDown={handleKeyDown}>
          <TextInput
            name='password'
            placeholder='비밀번호'
            type='password'
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            design={INPUT_DESIGN}
          />
        </InputBox>
        <ErrorText>{errorMessage ?? ''}</ErrorText>
        <TextButton
          text='로그인'
          width='100%'
          handleClick={handleSignin}
          isLoading={inhouseSigninMutation.isPending}
        />
      </Card>
    </Wrapper>
  );
};

export default Signin;
